import React from 'react';

import { Layout, Sidebar } from '../../../shared/ui';
import {
    useSectorCorrelation,
    useSectorDashboard,
    useSectorPerformance,
    useSectorValuation,
} from './api/sectorRotationApi';
import { MacroContextBanner } from './components/MacroContextBanner';
import { SectorCorrelationHeatmap } from './components/SectorCorrelationHeatmap';
import { SectorPerformance } from './components/SectorPerformance';
import { SectorRankingsTable } from './components/SectorRankingsTable';
import { SectorRRGChart } from './components/SectorRRGChart';
import { SectorValuationTable } from './components/SectorValuationTable';
import { formatDate } from './utils/formatters';

// Institutional Sector Rotation dashboard. The single ``/dashboard/
// sector_analytics`` call powers the macro banner + RRG + rankings (it embeds
// the fused macro/AFML tactical allocation, the RRG rankings and the
// correlation matrix). Valuations are fetched separately since they carry the
// per-ETF holdings drill-down.
export const SectorRotationPage: React.FC = () => {
    const {
        data: dashboard,
        isLoading: isDashLoading,
        error: dashError,
    } = useSectorDashboard();
    // The dashboard already embeds correlation, but we also expose the
    // dedicated endpoint so the heatmap keeps working even if the dashboard
    // correlation is null on a partial recompute.
    const { data: correlation } = useSectorCorrelation();
    const { data: valuations, isLoading: isValLoading } = useSectorValuation();
    // Sector Performance Analytics: pre-computed multi-window returns (O(1) read).
    const { data: performance, isLoading: isPerfLoading } = useSectorPerformance();

    const isLoading = isDashLoading || isValLoading || isPerfLoading;

    // Global Loading State
    if (isLoading) {
        return (
            <Layout sidebar={<Sidebar />}>
                <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
                    <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                    <div className="text-sm font-semibold text-slate-500 uppercase tracking-widest animate-pulse">
                        Loading Sector Engine...
                    </div>
                </div>
            </Layout>
        );
    }

    // Error State
    if (dashError || !dashboard) {
        return (
            <Layout sidebar={<Sidebar />}>
                <div className="p-4 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg shadow-sm dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800">
                    <h3 className="font-bold text-lg">Data Connection Failed</h3>
                    <p className="text-sm mt-1">
                        Unable to reach the Sector Rotation API. Please verify your
                        authentication token or backend health. If this is a fresh
                        deploy, trigger the pipeline first (ops key
                        <code className="mx-1 font-mono">sector_rotation</code>).
                    </p>
                </div>
            </Layout>
        );
    }

    const asOf = dashboard.rankings[0]?.date ?? correlation?.date ?? null;
    // Prefer the dashboard's embedded correlation; fall back to the dedicated
    // endpoint so the heatmap never blanks out.
    const corr = dashboard.correlation ?? correlation ?? null;

    return (
        <Layout sidebar={<Sidebar />}>
            <div className="space-y-6 text-slate-900 dark:text-slate-100 pb-12">
                {/* Header */}
                <header className="mb-2 flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-4">
                    <div>
                        <h1 className="text-3xl font-extrabold tracking-tight">
                            Sector Rotation Analytics
                        </h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
                            Identify winning sectors early, detect weakening trends before they reverse,
                            and make smarter portfolio allocation decisions with confidence.
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                            As Of
                        </div>
                        <div className="text-sm font-bold text-slate-700 dark:text-slate-300">
                            {formatDate(asOf)}
                        </div>
                    </div>
                </header>

                {/* MACRO + AFML TACTICAL BANNER */}
                <MacroContextBanner tactical={dashboard.tactical} />

                {/* RRG + RANKINGS ROW */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <div className="h-full">
                        <SectorRRGChart data={dashboard.rankings} />
                    </div>
                    <div className="h-full">
                        <SectorRankingsTable data={dashboard.rankings} />
                    </div>
                </div>

                {/* SECTOR PERFORMANCE ANALYTICS */}
                <SectorPerformance data={performance} />

                {/* CORRELATION MATRIX */}
                <SectorCorrelationHeatmap data={corr} />

                {/* VALUATIONS + HOLDINGS */}
                <SectorValuationTable data={valuations} />
            </div>
        </Layout>
    );
};

export default SectorRotationPage;
